import { SettingOutlined } from "@ant-design/icons";
import { keyOf } from "@p0security/shared/graph/graph";
import { ConnectedNode } from "@p0security/shared/graph/types";
import { Alert } from "antd";
import {
  GraphVisualization,
  NodeFlowEdge,
  NodeFlowRenderer,
  NodeFlowSpec,
  NodeHighlight,
  NodePropertiesRenderer,
} from "components/Assessment/components/GraphVisualization";
import { DiscoverVisualizationContext } from "components/Assessment/contexts/DiscoverVisualizationContext";
import { ColorBox } from "components/common/ColorBox";
import { VerticalSpacedDiv } from "components/divs";
import { compact, flatten, mapValues, uniqBy } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import React from "react";
import { colors } from "styles/colors";
import { join } from "utils/join";

import {
  Collapsed,
  CollapsedNode,
  GraphEdge,
  collapseLikeType,
  collapseLinearPath,
} from "./collapse";
import { collapsibleRender } from "./collapsible";
import { layout } from "./layout";
import { convertFlowSize } from "./sizes";
import { DiscoverRenderArgs, DiscoverVisualizationProps } from "./types";

const DEFAULT_MAX_RESULTS = 1;
const DEFAULT_MAX_PATHS = 1;
// Minimum path length needed to highlight search terms
const MIN_HIGHLIGHT_LENGTH = 4;

const RenderContext = React.createContext<DiscoverRenderArgs>({
  provider: "all",
  open: {},
});

/** Renders a graph visualization of queried paths
 */
export const DiscoverVisualization = <G extends object>(
  props: DiscoverVisualizationProps<G>
) => {
  const {
    collapse,
    fitView,
    fitViewOptions,
    highlight,
    matches,
    minimap,
    properties,
    render,
    settings,
    viewStyle,
    ...divProps
  } = props;
  const { provider, open, setInstance } = useContext(
    DiscoverVisualizationContext
  );
  const renderArgs = useMemo<DiscoverRenderArgs>(
    () => ({ provider, open }),
    [open, provider]
  );

  // graphKey is a monotonically increasing number, used as the key on the visualization
  // component. When the key changes, the visualization component will completely unmount and
  // remount into a new lifecycle. This is useful for resetting the visualization including
  // re-centering the graph
  const [graphKey, updateGraphKey] = useReducer((k) => k + 1, 0);
  useEffect(() => updateGraphKey(), [matches]);

  const fullDescriber: NodePropertiesRenderer<G & Collapsed<G>> = useCallback(
    (node: any) => (node.type === "_collapsed" ? null : properties(node)),
    [properties]
  );

  const fullRenderer: NodeFlowRenderer<G & Collapsed<G>, DiscoverRenderArgs> =
    useMemo(
      () =>
        ({
          ...mapValues(render, (r) => ({
            ...r,
            size: () => convertFlowSize(r.size).large,
          })),
          _collapsed: collapsibleRender(!!highlight)(render),
        }) as any,
      [highlight, render]
    );

  const { nodes, edges, warnings } = useMemo(() => {
    const before = performance.now();

    const noDataWarning =
      matches.length === 0 ? (
        <span key="no-data">
          Query returned no data. Please modify your search.
        </span>
      ) : null;

    const resultLimitWarning =
      matches.length > (settings.maxResults ?? DEFAULT_MAX_RESULTS) ? (
        <span key="result-limit">
          Query returned {matches.length} results, of which the first{" "}
          {settings.maxResults ?? DEFAULT_MAX_RESULTS} are shown.
        </span>
      ) : null;

    let maxPathLength = 0;
    let pathLimitWarning: JSX.Element | null = null;
    const limited = matches.slice(
      0,
      settings.maxResults ?? DEFAULT_MAX_RESULTS
    );
    const edges: GraphEdge<ConnectedNode<G, keyof G> & NodeHighlight>[] = [];

    // These nodes do not connect to any other nodes (e.g. if there is no query)
    const isolatedNodes: Record<string, ConnectedNode<G, keyof G>> = {};
    const isComplex = limited.some((n) =>
      n.matches.some((m) =>
        m.paths.some((p) => p.length >= MIN_HIGHLIGHT_LENGTH)
      )
    );
    for (const n of limited) {
      if (n.matches.every((m) => m.paths.every((p) => p.length < 2))) {
        isolatedNodes[keyOf(n.node)] = n.node;
      }
      for (const m of n.matches) {
        if (m.paths.length > (settings.maxPaths ?? DEFAULT_MAX_PATHS)) {
          pathLimitWarning = (
            <span key="path-limit">
              Query returned one or more matches for which only the first{" "}
              {settings.maxPaths ?? DEFAULT_MAX_PATHS} paths are shown.
            </span>
          );
        }
        for (const p of m.paths.slice(
          0,
          settings.maxPaths ?? DEFAULT_MAX_PATHS
        )) {
          let s = p[0];
          maxPathLength = Math.max(maxPathLength, p.length);
          for (const n of p.slice(1)) {
            const highlightEnd = n === p.at(-1);
            const highlightStart = s === p.at(0);
            edges.push([
              { ...s, highlight: isComplex && highlight && highlightStart },
              { ...n, highlight: isComplex && highlight && highlightEnd },
            ]);
            s = n;
          }
        }
      }
    }
    const uniqueEdges = uniqBy(edges, (e) => e.map(keyOf));

    const typeCollapsedEdges = collapseLikeType(uniqueEdges);
    const collapsedEdges = collapse
      ? collapseLinearPath<G>(typeCollapsedEdges, collapse)
      : typeCollapsedEdges;

    const highlightWarning = isComplex ? (
      <div>
        Query matches are highlighted in{" "}
        <ColorBox color={colors.fill.highlight} /> yellow.
      </div>
    ) : null;

    const howToFixWarning =
      resultLimitWarning || pathLimitWarning ? (
        <div key="edit-info">
          You can edit these limits using the <SettingOutlined /> control above.
        </div>
      ) : undefined;

    const uniqueNodes = uniqBy(
      [
        ...(Object.values(isolatedNodes) as CollapsedNode<G>[]),
        ...flatten(collapsedEdges),
      ],
      (n) => keyOf(n)
    );

    const dagreGraph = layout({
      nodes: uniqueNodes,
      edges: collapsedEdges,
      maxPathLength,
      render: fullRenderer,
      renderArgs,
    });

    const flowNodes: NodeFlowSpec<G & Collapsed<G>>[] = uniqueNodes.map((n) => {
      const d = dagreGraph.node(keyOf(n));
      return {
        inner: n,
        display: "inner" as const,
        height: d.height,
        width: d.width,
        position: {
          x: d.x - 0.5 * d.width,
          y: d.y - 0.5 * d.height,
        },
      };
    });
    const flowEdges: NodeFlowEdge<G & Collapsed<G>>[] = collapsedEdges.map(
      ([parent, child]) => ({
        parent,
        child,
      })
    );
    const after = performance.now();
    /* eslint-disable no-console */
    console.log(
      "Time to construct graph visualization",
      (after - before).toFixed(1),
      "ms"
    );
    console.log("  nodes:", flowNodes.length, "edges:", flowEdges.length);
    /* eslint-enable no-console */
    return {
      nodes: flowNodes,
      edges: flowEdges,
      warnings: compact([
        highlightWarning,
        noDataWarning,
        resultLimitWarning,
        pathLimitWarning,
        howToFixWarning,
      ]),
    };
  }, [
    collapse,
    fullRenderer,
    highlight,
    matches,
    renderArgs,
    settings.maxPaths,
    settings.maxResults,
  ]);

  return (
    <VerticalSpacedDiv {...divProps}>
      {warnings.length ? (
        <div
          style={{
            left: "+12px",
            height: 0,
            marginTop: "0",
            position: "relative",
            width: "fit-content",
            top: "+20px",
            zIndex: "1",
          }}
        >
          <Alert
            type="warning"
            message={join(warnings, " ")}
            // Float over graph visualization
            style={{
              maxWidth: "1000px",
            }}
          />
        </div>
      ) : null}
      <RenderContext.Provider value={renderArgs}>
        <GraphVisualization<G & Collapsed<G>, DiscoverRenderArgs>
          describer={fullDescriber}
          edges={edges}
          fitView={fitView}
          fitViewOptions={fitViewOptions}
          key={graphKey}
          minimap={minimap}
          nodes={nodes}
          onInit={setInstance}
          renderContext={RenderContext}
          renderer={fullRenderer}
          viewStyle={viewStyle}
        />
      </RenderContext.Provider>
    </VerticalSpacedDiv>
  );
};
